import { useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useNetworkStore } from '@/lib/store/useNetworkStore'
import { useAppStore } from '@/lib/store/useAppStore'
import { syncService } from '@/lib/sync/SyncService'
import { runInitialSync } from '@/lib/db/initialSync'
import { useSyncProgressStore } from '@/lib/store/useSyncProgressStore'

export function useSyncEngine() {
  const queryClient = useQueryClient()
  const setOnline = useNetworkStore((s) => s.setOnline)
  const { start, setProgress, done } = useSyncProgressStore()

  useEffect(() => {
    const handleOnline = async () => {
      setOnline(true)
      // Drain any queued offline writes
      await syncService.processQueue(queryClient)
      // Re-sync all data to pick up changes made by other devices
      const restaurantId = useAppStore.getState().restaurantId
      if (restaurantId) {
        start()
        runInitialSync(restaurantId, setProgress)
          .then(() => Promise.all([
            queryClient.refetchQueries({ queryKey: ['pos-report-eod'], type: 'all' }),
            queryClient.refetchQueries({ queryKey: ['pos-report-items'], type: 'all' }),
            queryClient.refetchQueries({ queryKey: ['pos-report-daily'], type: 'all' }),
            queryClient.refetchQueries({ queryKey: ['pos-day-session'], type: 'all' }),
          ]))
          .catch(console.error)
          .finally(done)
      }
    }
    const handleOffline = () => setOnline(false)

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)
    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [queryClient, setOnline, start, setProgress, done])
}
